import { DashboardTileType, IInfoTileInfo, ITableTileData, ITableTileInfo } from "@components/dashboard";
import { IColumn, IRow } from "@components/table";
import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    DocumentBusinessPartnerEntity,
    EntitySetName,
    IPrEmploymentEntity,
    IPrPayrollDocumentEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrPayrollDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { ClearedStatusCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { userNameWithAvatarFormatter } from "@pages/admin/users/Users.utils";
import { EmploymentStatus, getFilterForTabId } from "@pages/payroll/employment/Employment.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import customFetch from "@utils/customFetch";
import { isPayrollEnabledOnCompany } from "@utils/definition.utils";
import { logger } from "@utils/log";
import i18next from "i18next";

import { REST_API_URL } from "../../constants";
import { Status, TextAlign } from "../../enums";
import CurrencyType from "../../types/Currency";
import DateType from "../../types/Date";
import NumberType from "../../types/Number";

// TODO: uncomment CPs after implementing

const EMPLOYEE_OVERVIEW_DATA_URL = `${REST_API_URL}/DashboardData/Employees`;
const EMPLOYMENT_OVERVIEW_DATA_URL = `${REST_API_URL}/DashboardData/EmploymentOverview`;
const TAXPAYER_DECLARATION_DATA_URL = `${REST_API_URL}/DashboardData/TaxpayerDeclarations`;

interface IEmployeeOverviewData {
    AverageLengthOfEmployment: number;
    CurrentNumberOfEmployees: number;
    EmployeesLeftNumber: number;
    NewEmployeesThisYear: number;
}

interface IEmploymentOverviewData {
    Total: number;
    Hpp: number;
    HppFte: number;
    Dpc: number;
    Dpp: number;
}

interface ITaxPayerDeclarationData {
    Signed: number;
    Unsigned: number;
}

async function getEmployeeOverviewData(): Promise<IEmployeeOverviewData> {
    const res = await customFetch(EMPLOYEE_OVERVIEW_DATA_URL);
    return await res.json() as IEmployeeOverviewData;
}

async function getEmploymentOverviewData(): Promise<IEmploymentOverviewData> {
    const res = await customFetch(EMPLOYMENT_OVERVIEW_DATA_URL);
    return await res.json() as IEmploymentOverviewData;
}

async function getTaxpayerDeclarationData(): Promise<ITaxPayerDeclarationData> {
    const res = await customFetch(TAXPAYER_DECLARATION_DATA_URL);
    return await res.json() as ITaxPayerDeclarationData;
}

function addSignToNumber(num: number): string {
    return num > 0 ? `+${num}` : num.toString();
}

export function getEmployeeOverviewTileDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:EmployeeOverview.Heading"),
        link: null,
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        infoData: async ({ context, oData, signal }) => {
            const data = await getEmployeeOverviewData();
            return [{
                value: data.CurrentNumberOfEmployees,
                label: i18next.t("Home:EmployeeOverview.CurrentEmployeesCount")
            }, {
                value: addSignToNumber(data.NewEmployeesThisYear),
                severity: Status.Success,
                label: i18next.t("Home:EmployeeOverview.OnboardedThisYear")
            }, {
                value: data.EmployeesLeftNumber * -1, // https://solitea-cz.atlassian.net/browse/DEV-43117
                severity: Status.Error,
                label: i18next.t("Home:EmployeeOverview.OffboardedThisYear")
            }, {
                value: NumberType.format(data.AverageLengthOfEmployment, { maximumFractionDigits: 1 }),
                label: i18next.t("Home:EmployeeOverview.AverageEmploymentLength"),
                unit: i18next.t("Common:Time.Year", { count: Math.round(data.AverageLengthOfEmployment) }),
            }];
        },
        size: { w: 4, h: 1 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfo]
    };
}

export function getEmploymentOverviewTileDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:EmploymentOverview.Heading"),
        link: null,
        firstLetterUppercase: true,
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        infoData: async ({ context, oData, signal }) => {
            const data = await getEmploymentOverviewData();
            return [{
                value: data.Hpp,
                label: i18next.t("Home:EmploymentOverview.FullTime")
            }, {
                value: data.Dpp,
                label: i18next.t("Home:EmploymentOverview.DPP")
            }, {
                value: data.Dpc,
                label: i18next.t("Home:EmploymentOverview.DPC")
            }, {
                value: NumberType.format(data.HppFte, { maximumFractionDigits: 2 }),
                label: i18next.t("Home:EmploymentOverview.FullTimeEquivalent")
            }];
        },
        size: { w: 4, h: 1 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfo]
    };
}

export function getTaxpayerDeclarationTileDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:TaxpayerDeclaration.Heading"),
        link: null,
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        infoData: async ({ context, oData, signal }) => {
            const data = await getTaxpayerDeclarationData();
            return [{
                value: data.Signed,
                severity: Status.Success,
                label: i18next.t("Home:TaxpayerDeclaration.Signed")
            }, {
                value: data.Unsigned,
                severity: Status.Error,
                label: i18next.t("Home:TaxpayerDeclaration.NotSigned")
            }];
        },
        size: { w: 2, h: 1 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfo]
    };
}


interface ISalaryAveragesData {
    AverageGrossSalary: number;
    AverageGrossSalaryLastQuarter: number;
}

const SALARY_AVERAGES_DATA_URL = `${REST_API_URL}/DashboardData/SalaryAverages`;

export async function getSalaryAveragesData(): Promise<ISalaryAveragesData> {
    const res = await customFetch(SALARY_AVERAGES_DATA_URL);
    return await res.json() as ISalaryAveragesData;
}

export function getSalaryAveragesTileDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:SalaryAverages.Heading"),
        link: null,
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        infoData: async ({ context, oData, signal }) => {
            const data = await getSalaryAveragesData();
            return [{
                value: NumberType.format(data.AverageGrossSalary, { maximumFractionDigits: 0 }),
                label: i18next.t("Home:SalaryAverages.Total"),
                unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context))
            }, {
                value: NumberType.format(data.AverageGrossSalaryLastQuarter, { maximumFractionDigits: 0 }),
                label: i18next.t("Home:SalaryAverages.LastQuarter"),
                unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context))
            }];
        },
        size: { w: 4, h: 1 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfo]
    };
}

async function getEmploymentsTableData(oData: OData): Promise<ITableTileData> {
    const rows: IRow[] = [];
    const columns: IColumn[] = [
        { id: "NumberOurs", label: i18next.t("Home:EmploymentsTable.NumberOurs"), textAlign: TextAlign.Left },
        { id: "Name", label: i18next.t("Home:EmploymentsTable.Name"), textAlign: TextAlign.Left },
        { id: "Employee", label: i18next.t("Home:EmploymentsTable.Employee"), textAlign: TextAlign.Left },
        { id: "type", label: i18next.t("Home:EmploymentsTable.Type"), textAlign: TextAlign.Left },
        { id: "dateStart", label: i18next.t("Home:EmploymentsTable.StartDate"), textAlign: TextAlign.Right },
        { id: "dateEnd", label: i18next.t("Home:EmploymentsTable.EndDate"), textAlign: TextAlign.Right },
    ];

    try {
        const res = await oData.getEntitySetWrapper(EntitySetName.PrEmployments)
            .query()
            .filter(getFilterForTabId(EmploymentStatus.Active))
            .select("Id", PrEmploymentEntity.DateEnd, PrEmploymentEntity.DateStart, PrEmploymentEntity.NumberOurs, PrEmploymentEntity.Name)
            .expand(PrEmploymentEntity.Employee, (q) => q.select("Id"))
            .expand(PrEmploymentEntity.Type, (q) => q.select("Name"))
            .expand(PrEmploymentEntity.Employee, (q) => q.select(PrEmployeeEntity.Id, PrEmployeeEntity.FirstName, PrEmployeeEntity.LastName))
            .orderBy(PrEmploymentEntity.NumberOurs, false)
            .fetchData<IPrEmploymentEntity[]>();


        for (const r of res.value) {
            rows.push({
                id: r.Id,
                values: {
                    NumberOurs: r.NumberOurs,
                    Name: r.Name,
                    Employee: userNameWithAvatarFormatter(r.Employee.FirstName, { entity: r.Employee }, {
                        isBold: false,
                        withLink: false
                    }),
                    type: r.Type?.Name,
                    dateStart: DateType.format(r.DateStart, null, { isReadOnly: true }),
                    dateEnd: DateType.format(r.DateEnd, null, { isReadOnly: true })
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
}

export function getEmploymentsTableDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:EmploymentsTable.Heading"),
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        tableData: async ({ context, oData, signal }): Promise<ITableTileData> => {
            return await getEmploymentsTableData(oData);
        },
        size: { w: 7, h: 2 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfoConfidential]
    };
}

async function getPROtherLiabilitiesTableData(oData: OData): Promise<ITableTileData> {
    const rows: IRow[] = [];
    const columns: IColumn[] = [
        { id: "NumberOurs", label: i18next.t("Home:PayrollOtherLiabilities.Number"), textAlign: TextAlign.Left },
        { id: "BusinessPartner", label: i18next.t("Home:PayrollOtherLiabilities.CounterPart"),
            textAlign: TextAlign.Left, width: 200 },
        { id: "Total", label: i18next.t("Home:PayrollOtherLiabilities.Total"), textAlign: TextAlign.Right },
        { id: "DateDue", label: i18next.t("Home:PayrollOtherLiabilities.DateDue"), textAlign: TextAlign.Right },
    ];

    try {
        const res = await oData.getEntitySetWrapper(EntitySetName.PrPayrollDocuments)
            .query()
            .filter(`${PrPayrollDocumentEntity.ClearedStatusCode} ne '${ClearedStatusCode.Cleared}' AND ${PrPayrollDocumentEntity.DocumentTypeCode} eq '${DocumentTypeCode.OtherLiability}'`)
            .select(PrPayrollDocumentEntity.Id, PrPayrollDocumentEntity.NumberOurs, PrPayrollDocumentEntity.DateDue, PrPayrollDocumentEntity.TransactionAmount, PrPayrollDocumentEntity.TransactionCurrencyCode)
            .expand(PrPayrollDocumentEntity.BusinessPartner, (q) => {
                return q.select(DocumentBusinessPartnerEntity.Name, DocumentBusinessPartnerEntity.LegalNumber)
                    .expand(DocumentBusinessPartnerEntity.Employee, q2 => q2.select(PrEmployeeEntity.Id, PrEmployeeEntity.FirstName, PrEmployeeEntity.LastName));
            })
            .orderBy(PrPayrollDocumentEntity.NumberOurs, false)
            .fetchData<IPrPayrollDocumentEntity[]>();

        for (const r of res.value) {
            const hasEmployeeBP = !!r.BusinessPartner?.Employee?.Id;
            const BusinessPartner = hasEmployeeBP ?
                userNameWithAvatarFormatter(r.BusinessPartner.Employee.FirstName, { entity: r.BusinessPartner.Employee }, {
                    isBold: false,
                    withLink: false
                }) : r.BusinessPartner?.Name ?? r.BusinessPartner?.LegalNumber;

            rows.push({
                id: r.Id,
                values: {
                    NumberOurs: r.NumberOurs,
                    BusinessPartner,
                    Total: CurrencyType.format(r.TransactionAmount, { currency: r.TransactionCurrencyCode }),
                    DateDue: DateType.format(r.DateDue)
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
}
export function getPROtherLiabilitiesTableTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:PayrollOtherLiabilities.Heading"),
        isVisible: ({ context }) => isPayrollEnabledOnCompany(context.getCompany()),
        tableData: async ({ context, oData, signal }): Promise<ITableTileData> => {
            return await getPROtherLiabilitiesTableData(oData);
        },
        size: { w: 4, h: 2 },
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfoConfidential]
    };
}